import React, {useEffect, useRef, useState} from "react";
import "./styles.css";
import {instance} from "../../../utils/utils";
import TrainingCard from "./trainingCard";
import TrainingInfoModal from "./trainingInfoModal";
import {parse, format} from "date-fns";
import Popover from "../../popover/popover";

const TRAINING_TYPE_CHOICES = [
    [10, "Valoración física / Control"],
    [20, "Valoración nutricional / Control"],
    [30, "Programa cardio libre"],
    [40, "Entrenamiento 1 a 1"],
    [50, "Clase grupal virtual"],
    [60, "Video pregrabado"],
    [70, "Día de descanso"],
    [80, "Sesión de Estiramiento"],
    [90, "Entrenamiento de Fuerza"],
    [100, "Fisioterapia y recuperación"],
    [110, "Entrenamiento libre"],
    [120, "No aplica"],
];

const TRAINING_TIME_CHOICES = [
    [10, "10 min"],
    [20, "20 min"],
    [30, "30 min"],
    [40, "40 min"],
    [50, "50 min"],
    [60, "60 min"],
    [90, "90 min"],
    [120, "120 min"],
    [130, "No aplica"],
];

const CLASS_TYPE_CHOICES = [
    [10, "Acompañamiento presencial"],
    [20, "Acompañamiento virtual"],
    [30, "Solo"],
    [40, "No aplica"],
];

const INTENSITY_CHOICES = [
    [10, "Baja"],
    [20, "Media"],
    [30, "Alta"],
    [40, "No aplica"],
];

const ACTIVITY_TYPE_CHOICES = [
    [10, "Anaeróbica"],
    [20, "Aeróbica"],
    [30, "Recuperación"],
    [40, "Prueba"],
    [50, "No aplica"],
];

const OBJECTIVE_CHOICES = [
    [10, "Quema calórica"],
    [20, "Desarrollo de masa muscular"],
    [30, "Acondicionamiento físico"],
    [40, "Valoración y prueba"],
    [50, "Recuperación"],
    [60, "Desarrollo de movilidad"],
    [70, "Desarrollo de agilidad"],
    [80, "Fortalecimiento"],
    [90, "Desarrollo de potencia"],
    [100, "Desarrollo de velocidad"],
    [110, "Desarrollo cardiovascular"],
    [120, "No aplica"],
];

const METHODOLOGY_CHOICES = [
    [10, "Intervalos"],
    [20, "Resistencia"],
    [30, "Por estaciones"],
    [40, "Falla muscular"],
    [50, "Máximos / PRs"],
    [60, "EMOM"],
    [70, "AMRAP"],
    [80, "TABATA"],
    [90, "Carrera continua"],
    [100, "No aplica"],
];

const HEART_ZONE_CHOICES = [
    [10, "1"],
    [20, "2"],
    [30, "3"],
    [40, "4"],
    [50, "5"],
    [60, "1 y 2"],
    [70, "2 y 3"],
    [80, "3 y 4"],
    [90, "4 y 5"],
    [100, "1, 2, 3"],
    [110, "4, 5, 6"],
    [120, "No aplica"],
];

const BODY_ZONE_CHOICES = [
    [10, "Tren inferior"],
    [20, "Tren superior"],
    [30, "Core"],
    [40, "Zona específica"],
    [50, "Todo el cuerpo"],
    [60, "No aplica"],
];

const ADVANCED_OPTIONS = [
    {label: "Entrenamiento", key: "training", options: TRAINING_TYPE_CHOICES},
    {
        label: "Duración del entrenamiento",
        key: "training_time",
        options: TRAINING_TIME_CHOICES,
    },
    {label: "Tipo de clase", key: "class_type", options: CLASS_TYPE_CHOICES},
    {label: "Intensidad", key: "intensity", options: INTENSITY_CHOICES},
    {
        label: "Tipo de actividad",
        key: "activity_type",
        options: ACTIVITY_TYPE_CHOICES,
    },
    {label: "Objetivo", key: "objective", options: OBJECTIVE_CHOICES},
    {label: "Metodología", key: "methodology", options: METHODOLOGY_CHOICES},
    {
        label: "Zona de frecuencia cardíaca",
        key: "heart_rate_zone",
        options: HEART_ZONE_CHOICES,
    },
    {
        label: "Zona corporal de trabajo",
        key: "body_zone",
        options: BODY_ZONE_CHOICES,
    },
];

const TrainingFormModal = ({
    show,
    onClose,
    trainingPlanId,
    sessionId = null,
    onRefresh,
}) => {
    const [formData, setFormData] = useState({
        class_name: "",
        teacher_name: "",
        notes: "",
        options: "",
        has_record: false,
        is_new_record: false,
        is_duplicated: false,
        launch_date: "",
        launch_time: "00:00",
        is_time_metric: false,
        is_inverse_metric: false
    });
    const [recordsOptions, setRecordsOptions] = useState([]);
    const [duplicateResults, setDuplicateResults] = useState([]);
    const [searchDate, setSearchDate] = useState("");
    const [selectedDuplicateId, setSelectedDuplicateId] = useState(null);
    const [selectedDuplicate, setSelectedDuplicate] = useState(null);
    const [infoModalVisible, setInfoModalVisible] = useState(false);
    const [infoModalConfig, setInfoModalConfig] = useState({
        title: "",
        description: "",
        primaryAction: null,
        secondaryAction: null,
    });
    const [inputType, setInputType] = useState("text");
    const [showOptions, setShowOptions] = useState(false);
    const dropdownRef = useRef(null);

    const handleFocus = () => {
        const [dd, mm, yyyy] = formData.launch_date.split("/");
        if (dd && mm && yyyy) {
            const isoDate = `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
            handleChange("launch_date", isoDate);
        }
        setInputType("date");
    };

    const handleBlur = (e) => {
        const rawValue = e.target.value;

        if (rawValue) {
            const [yyyy, mm, dd] = rawValue.split("-");
            if (yyyy && mm && dd) {
                const formatted = `${dd.padStart(2, "0")}/${mm.padStart(
                    2,
                    "0"
                )}/${yyyy}`;
                handleChange("launch_date", formatted);
            }
        }

        setInputType("text");
    };

    const formatDateToInput = (ddmmyyyy) => {
        if (!ddmmyyyy || typeof ddmmyyyy !== "string") return "";
        const [day, month, year] = ddmmyyyy.split("-");
        if (!day || !month || !year) return "";
        return `${day}-${month}-${year}`;
    };

    const normalize = (str) => String(str).toLowerCase().trim();

    const getKeyFromValue = (choices, incomingValue) => {
        const normIncoming = normalize(incomingValue);
        const found = choices.find(
            ([key, label]) =>
                normalize(label) === normIncoming || String(key) === normIncoming
        );
        return found ? found[0] : "";
    };

    const handleChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const isFormValid =
        !!formData.class_name &&
        !!formData.teacher_name &&
        !!formData.launch_date &&
        (formData.has_record
            ? !!formData.selected_record
            : formData.is_new_record
                ? !!formData.new_record_name && !!formData.new_record_unit
                : formData.is_duplicated
                    ? !!selectedDuplicateId
                    : true);

    const handleCheckboxChange = (field) => {
        setFormData((prev) => {
            const newValue = !prev[field];
            return {
                ...prev,
                has_record: false,
                is_new_record: false,
                is_duplicated: false,
                [field]: newValue,
            };
        });
    };

    const handleSubmit = async (confirmed = false) => {
        if (!confirmed) {
            if (
                !formData.has_record &&
                !formData.is_new_record &&
                !formData.is_duplicated
            ) {
                return triggerInfoModal("confirm_publish");
            }

            if (formData.has_record && recordsOptions.length === 0) {
                return triggerInfoModal("record_not_found");
            }

            if (
                formData.has_record &&
                recordsOptions.some(
                    (r) =>
                        r.id === Number(formData.selected_record) &&
                        normalizeDate(r.date) === formData.launch_date
                )
            ) {
                return triggerInfoModal("record_duplicate_warning");
            }

            if (
                formData.is_new_record &&
                recordsOptions.some(
                    (r) =>
                        r.name.trim().toLowerCase() ===
                        (formData.new_record_name || "").trim().toLowerCase()
                )
            ) {
                return triggerInfoModal("record_name_exists");
            }

            if (formData.is_duplicated && duplicateResults.length === 0) {
                return triggerInfoModal("duplicate_empty");
            }

            if (
                formData.is_duplicated &&
                selectedDuplicate &&
                normalizeDate(selectedDuplicate.launch_date) === formData.launch_date &&
                normalizeTime(selectedDuplicate.launch_time) ===
                normalizeTime(formData.launch_time)
            ) {
                return triggerInfoModal("duplicate_same_datetime");
            }

            if (formData.is_duplicated) {
                return triggerInfoModal("duplicate_success");
            }

            if (formData.is_new_record) {
                return triggerInfoModal("new_record_success");
            }
        }

        const [dd, mm, yyyy] = formData.launch_date.split("/");
        const isoDate = `${yyyy}-${mm}-${dd}`;
        const day_of_week = new Date(isoDate).getDay();

        try {
            const baseItem = {
                class_name: formData.class_name,
                teacher_name: formData.teacher_name,
                launch_date: formData.launch_date?.split("/").join("-"),
                launch_time: formData.launch_time || "00:00",
                day_of_week: day_of_week,
                training: Number(formData.training) || undefined,
                training_time: Number(formData.training_time) || undefined,
                class_type: Number(formData.class_type) || undefined,
                intensity: Number(formData.intensity) || undefined,
                activity_type: Number(formData.activity_type) || undefined,
                objective: Number(formData.objective) || undefined,
                methodology: Number(formData.methodology) || undefined,
                heart_rate_zone: Number(formData.heart_rate_zone) || undefined,
                body_zone: Number(formData.body_zone) || undefined,
                description: formData.description || "",
                status: sessionId ? 20 : 10,
                ...(formData.has_record && formData.selected_record
                    ? {max_record: Number(formData.selected_record)}
                    : {}),
                ...(formData.is_new_record &&
                formData.new_record_name &&
                formData.new_record_unit
                    ? {
                        new_record: {
                            name: formData.new_record_name,
                            unit_measurement: formData.is_time_metric ? '' : formData.new_record_unit,
                            is_time_metric: formData.is_time_metric,
                            is_inverse_metric: formData.is_inverse_metric,
                        },
                    }
                    : {}),
                ...(formData.is_duplicated && selectedDuplicateId
                    ? {duplicate_item_id: selectedDuplicateId}
                    : {}),
            };

            if (sessionId) {
                await instance.patch(
                    `memberships/new/training_plan/${trainingPlanId}/update`,
                    {
                        items: {
                            ...baseItem,
                            id: sessionId,
                        },
                    }
                );
            } else {
                await instance.post("/memberships/new/training_plan", {
                    training_plan_id: trainingPlanId,
                    items: [baseItem],
                });
            }

            onClose();
            onRefresh?.();
        } catch (err) {
            console.error("❌ Error al guardar:", err);
            triggerInfoModal("backend_error", getBackendError(err));
        }
    };

    const showInfoModal = ({
        title,
        description,
        primaryAction,
        secondaryAction,
    }) => {
        setInfoModalConfig({title, description, primaryAction, secondaryAction});
        setInfoModalVisible(true);
    };

    const handleClose = () => {
        if (formData.class_name || formData.teacher_name || formData.launch_date) {
            showInfoModal({
                title: "¿Estás seguro de cancelar esta creación?",
                description: "Los cambios no guardados se perderán.",
                primaryAction: {
                    label: "Seguir editando",
                    onClick: () => setInfoModalVisible(false),
                },
                secondaryAction: {
                    label: "Descartar cambios",
                    onClick: () => {
                        setInfoModalVisible(false);
                        resetForm();
                        onClose();
                    },
                },
            });
        } else {
            resetForm();
            onClose();
        }
    };

    const triggerInfoModal = (type, payload = null) => {
        // eslint-disable-next-line default-case
        switch (type) {
            case "confirm_publish":
                return showInfoModal({
                    title: "¿Deseas publicar esta clase grupal?",
                    description:
                        "A partir de este momento será visible para todos los alumnos asignados.",
                    primaryAction: {
                        label: sessionId ? "Editar Clase" : "Publicar",
                        onClick: () => {
                            setInfoModalVisible(false);
                            handleSubmit(true);
                        },
                    },
                    secondaryAction: {
                        label: "Cancelar",
                        onClick: () => setInfoModalVisible(false),
                    },
                });

            case "record_duplicate_warning":
                return showInfoModal({
                    title: "Este récord ya está vinculado a otra clase en la misma fecha",
                    description:
                        "Publicarlo nuevamente podría generar duplicidad de resultados.",
                    primaryAction: {
                        label: "Usar de todas formas",
                        onClick: () => {
                            setInfoModalVisible(false);
                            handleSubmit(true);
                        },
                    },
                    secondaryAction: {
                        label: "Cancelar",
                        onClick: () => setInfoModalVisible(false),
                    },
                });

            case "record_not_found":
                return showInfoModal({
                    title: "No hay récords existentes para asignar",
                    description: "Crea uno nuevo o revisa tus filtros.",
                    primaryAction: {
                        label: "Crear nuevo récord",
                        onClick: () => {
                            setFormData((prev) => ({
                                ...prev,
                                has_record: false,
                                is_new_record: true,
                            }));
                            setInfoModalVisible(false);
                        },
                    },
                    secondaryAction: {
                        label: "Volver",
                        onClick: () => setInfoModalVisible(false),
                    },
                });

            case "new_record_success":
                return showInfoModal({
                    title: "Nuevo récord creado y vinculado exitosamente",
                    description:
                        "Ahora puedes publicar el entrenamiento con este desafío incluido.",
                    primaryAction: {
                        label: "Publicar entrenamiento",
                        onClick: () => {
                            setInfoModalVisible(false);
                            handleSubmit(true);
                        },
                    },
                    secondaryAction: {
                        label: "Editar récord",
                        onClick: () => setInfoModalVisible(false),
                    },
                });

            case "record_name_exists":
                return showInfoModal({
                    title: "Ya existe un récord con este nombre",
                    description:
                        "Te recomendamos usar un nombre único para evitar confusión.",
                    primaryAction: {
                        label: "Usar nombre diferente",
                        onClick: () => setInfoModalVisible(false),
                    },
                });

            case "duplicate_success":
                return showInfoModal({
                    title: "Clase duplicada correctamente",
                    description: "Puedes editar los datos antes de publicarla",
                    primaryAction: {
                        label: "Editar clase",
                        onClick: () => setInfoModalVisible(false),
                    },
                    secondaryAction: {
                        label: "Publicar ahora",
                        onClick: () => {
                            setInfoModalVisible(false);
                            handleSubmit(true);
                        },
                    },
                });

            case "duplicate_empty":
                return showInfoModal({
                    title:
                        "No se encontraron entrenamientos disponibles en la fecha seleccionada.",
                    description: "",
                    primaryAction: {
                        label: "Elegir otra fecha",
                        onClick: () => setInfoModalVisible(false),
                    },
                    secondaryAction: {
                        label: "Cancelar",
                        onClick: () => setInfoModalVisible(false),
                    },
                });

            case "duplicate_same_datetime":
                return showInfoModal({
                    title:
                        "La nueva clase no puede tener la misma fecha y hora de publicación que la original",
                    description: "",
                    primaryAction: {
                        label: "Editar fecha",
                        onClick: () => setInfoModalVisible(false),
                    },
                    secondaryAction: {
                        label: "Cancelar",
                        onClick: () => setInfoModalVisible(false),
                    },
                });

            case "global_error":
                return showInfoModal({
                    title: "Ocurrió un error al guardar el entrenamiento",
                    description: "Verifica tu conexión e intenta de nuevo",
                    primaryAction: {
                        label: "Reintentar",
                        onClick: () => {
                            setInfoModalVisible(false);
                            handleSubmit(true);
                        },
                    },
                    secondaryAction: {
                        label: "Cancelar",
                        onClick: () => setInfoModalVisible(false),
                    },
                });

            case "cancel_edit":
                return showInfoModal({
                    title: "¿Estás seguro de cancelar esta creación?",
                    description: "Los cambios no guardados se perderán.",
                    primaryAction: {
                        label: "Seguir editando",
                        onClick: () => setInfoModalVisible(false),
                    },
                    secondaryAction: {
                        label: "Descartar cambios",
                        onClick: () => {
                            setInfoModalVisible(false);
                            onClose();
                        },
                    },
                });

            case "backend_error":
                return showInfoModal({
                    title: "Error al procesar la solicitud",
                    description: payload || "Ocurrió un error inesperado. Intenta de nuevo.",
                    primaryAction: {
                        label: "Reintentar",
                        onClick: () => {
                            setInfoModalVisible(false);
                            handleSubmit(true);
                        },
                    },
                    secondaryAction: {
                        label: "Cancelar",
                        onClick: () => setInfoModalVisible(false),
                    },
                });
        }
    };

    const resetForm = () => {
        setFormData({
            class_name: "",
            teacher_name: "",
            description: "",
            notes: "",
            launch_date: "",
            launch_time: "00:00",
            training: "120",
            training_time: "130",
            class_type: "40",
            intensity: "40",
            activity_type: "50",
            objective: "120",
            methodology: "100",
            heart_rate_zone: "120",
            body_zone: "60",
            has_record: false,
            is_new_record: false,
            is_duplicated: false,
            selected_record: "",
            new_record_name: "",
            new_record_unit: "",
            max_records: [],
        });
        setSelectedDuplicateId(null);
        setSelectedDuplicate(null);
        setSearchDate("");
        setRecordsOptions([]);
        setDuplicateResults([]);
    };

    const normalizeDate = (dateString) => {
        if (!dateString) return "";
        try {
            const parsed = parse(dateString, "dd-MM-yyyy", new Date());
            return format(parsed, "yyyy-MM-dd");
        } catch {
            return dateString;
        }
    };

    const normalizeTime = (timeStr) => {
        if (!timeStr) return "";
        const [hh, mm] = timeStr.split(":");
        return `${hh.padStart(2, "0")}:${mm.padStart(2, "0")}`;
    };

    const getBackendError = (err) => {
        let rawError =
            err?.response?.data?.[0] ||
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            err?.message ||
            "Ocurrió un error inesperado";

        if (Array.isArray(rawError)) {
            return rawError.join(" ");
        }

        if (typeof rawError === "string" && rawError.startsWith("['")) {
            try {
                const parsed = JSON.parse(
                    rawError.replace(/'/g, '"')
                );
                if (Array.isArray(parsed)) return parsed.join(" ");
                return parsed;
            } catch {
                return rawError.replace(/[\\[\]']/g, "");
            }
        }

        return rawError;
    };

    useEffect(() => {
        if (!sessionId) {
            // Modo crear
            setFormData({
                class_name: "",
                teacher_name: "",
                description: "",
                notes: "",
                launch_date: "",
                launch_time: "00:00",
                training: "120",
                training_time: "130",
                class_type: "40",
                intensity: "40",
                activity_type: "50",
                objective: "120",
                methodology: "100",
                heart_rate_zone: "120",
                body_zone: "60",
                has_record: false,
                is_new_record: false,
                is_duplicated: false,
                selected_record: "",
                new_record_name: "",
                new_record_unit: "",
                max_records: [],
            });
        } else {
            // Modo editar
            const fetchDetails = async () => {
                try {
                    const res = await instance.get(
                        `memberships/new/training_plan/${sessionId}/detail`
                    );
                    const d = res.data;

                    const record = d.max_records?.[0];

                    setFormData({
                        class_name: d.class_name || "",
                        teacher_name: d.teacher_name || "",
                        description: d.description || "",
                        launch_date: formatDateToInput(d.launch_date),
                        launch_time: d.launch_time || "09:00",
                        training: getKeyFromValue(TRAINING_TYPE_CHOICES, d.training),
                        training_time: getKeyFromValue(
                            TRAINING_TIME_CHOICES,
                            d.training_time
                        ),
                        class_type: getKeyFromValue(CLASS_TYPE_CHOICES, d.class_type),
                        intensity: getKeyFromValue(INTENSITY_CHOICES, d.intensity),
                        activity_type: getKeyFromValue(
                            ACTIVITY_TYPE_CHOICES,
                            d.activity_type
                        ),
                        objective: getKeyFromValue(OBJECTIVE_CHOICES, d.objective),
                        methodology: getKeyFromValue(METHODOLOGY_CHOICES, d.methodology),
                        heart_rate_zone: getKeyFromValue(
                            HEART_ZONE_CHOICES,
                            d.heart_rate_zone
                        ),
                        body_zone: getKeyFromValue(BODY_ZONE_CHOICES, d.body_zone),
                        max_records: d.max_records || [],
                        has_record: !!record,
                        selected_record: record?.id || "",
                    });
                } catch (err) {
                    console.error("❌ Error al cargar datos:", err);
                }
            };

            fetchDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    useEffect(() => {
        const fetchRecords = async () => {
            try {
                const res = await instance.get("trainings/corp/exercises");

                const data = res.data;
                if (!Array.isArray(data) || data.length === 0) {
                    setRecordsOptions([]);
                    triggerInfoModal("record_not_found");
                    return;
                }

                setRecordsOptions(data);
            } catch (error) {
                console.error("❌ Error al cargar records:", error);
                setRecordsOptions([]);
                triggerInfoModal("record_not_found");
            }
        };

        if (formData.has_record) {
            fetchRecords();
        }

        // triggerInfoModal no cambia, no es necesario como dependencia real
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.has_record]);

    useEffect(() => {
        const fetchDuplicates = async () => {
            try {
                const searchParams = new URLSearchParams({
                    training_plan_id: trainingPlanId,
                });

                if (searchDate) {
                    const [year, month, day] = searchDate.split("-");
                    searchParams.append("date", `${day}-${month}-${year}`);
                }

                const res = await instance.get(
                    `/memberships/new/training_plan/duplicate?${searchParams}`
                );

                const items = res.data?.items;

                if (!Array.isArray(items) || items.length === 0) {
                    setDuplicateResults([]);
                    triggerInfoModal("duplicate_empty");
                    return;
                }

                setDuplicateResults(items);
            } catch (error) {
                console.error("❌ Error al cargar duplicados:", error);
                setDuplicateResults([]);
                triggerInfoModal("duplicate_empty");
            }
        };

        if (formData.is_duplicated) {
            fetchDuplicates();
        }

        // triggerInfoModal no cambia, no es necesario como dependencia real
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.is_duplicated, searchDate, trainingPlanId]);

    useEffect(() => {
        if (!show) {
            resetForm();
        }
    }, [show]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowOptions(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    if (!show) return null;

    return (
        <>
            <div className="training-form-modal-overlay">
                <div className="training-form-modal-content p-5 gap-2">
                    <div className="overflow d-flex flex-column gap-2 w-100">
                        <div className="training-form-modal-close position-relative w-100">
                            <button
                                className="training-modal-close  position-absolute top-0 end-0 p-0 d-flex justify-content-end align-items-center"
                                onClick={handleClose}
                                aria-label="Cerrar modal"
                            >
                                <i className="bi bi-x"></i>
                            </button>
                        </div>

                        <h5 className="training-form-modal-title text-center fw-semibold mb-3">
                            Plan de entrenamiento
                        </h5>

                        <input
                            type="text"
                            placeholder="Nombre de la clase"
                            className="input-style-elevva training-form-modal-input"
                            value={formData.class_name}
                            onChange={(e) => handleChange("class_name", e.target.value)}
                        />

                        <input
                            type="text"
                            placeholder="Nombre del profesor"
                            className="input-style-elevva training-form-modal-input"
                            value={formData.teacher_name}
                            onChange={(e) => handleChange("teacher_name", e.target.value)}
                        />

                        <div className="d-flex justify-content-between flex-wrap w-100">
                            <div className="training-form-modal-form-check form-check">
                                <input
                                    className="training-form-modal-checkbox form-check-input"
                                    type="checkbox"
                                    value=""
                                    id="flexCheckDefault"
                                    checked={formData.has_record}
                                    onChange={() => handleCheckboxChange("has_record")}
                                />
                                <label
                                    className="training-form-modal-check-label"
                                    htmlFor="flexCheckDefault"
                                >
                                    Records
                                </label>
                            </div>

                            <div className="training-form-modal-form-check form-check">
                                <input
                                    className="training-form-modal-checkbox form-check-input"
                                    type="checkbox"
                                    value=""
                                    id="flexCheckDefault2"
                                    checked={formData.is_new_record}
                                    onChange={() => handleCheckboxChange("is_new_record")}
                                />
                                <label
                                    className="training-form-modal-check-label"
                                    htmlFor="flexCheckDefault2"
                                >
                                    Nuevo Record
                                </label>
                            </div>

                            <div className="training-form-modal-form-check form-check">
                                <input
                                    className="training-form-modal-checkbox form-check-input"
                                    type="checkbox"
                                    value=""
                                    id="flexCheckDefault3"
                                    checked={formData.is_duplicated}
                                    onChange={() => handleCheckboxChange("is_duplicated")}
                                />
                                <label
                                    className="training-form-modal-check-label"
                                    htmlFor="flexCheckDefault3"
                                >
                                    Duplicar
                                </label>
                            </div>
                        </div>

                        {formData.has_record && (
                            <div className="w-100 gap-2 d-flex flex-column justify-content-center">
                                <select
                                    className="form-select training-form-modal-dropdown"
                                    value={formData.selected_record || ""}
                                    onChange={(e) =>
                                        handleChange("selected_record", e.target.value)
                                    }
                                >
                                    <option value="">Records existentes</option>
                                    {recordsOptions.map((record) => (
                                        <option key={record.id} value={record.id}>
                                            {record.name}{" "}
                                            {record.unit_measurement && `(${record.unit_measurement})`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {formData.is_new_record && (
                            <>
                                <input
                                    type="text"
                                    placeholder="Nombre del Record"
                                    className="input-style-elevva training-form-modal-input"
                                    value={formData.new_record_name || ""}
                                    onChange={(e) => handleChange("new_record_name", e.target.value)}
                                />

                                <input
                                    type="text"
                                    placeholder="Unidad de medida (Kg, reps, m/s)"
                                    className="input-style-elevva training-form-modal-input"
                                    value={formData.new_record_unit || ""}
                                    disabled={formData.is_time_metric}
                                    required={!formData.is_time_metric}
                                    maxLength={5}
                                    onChange={(e) => handleChange("new_record_unit", e.target.value)}
                                />

                                <div>
                                    <div className="training-form-modal-form-check form-check">
                                        <input
                                            className="training-form-modal-checkbox form-check-input"
                                            type="checkbox"
                                            id="flexCheckDefault2"
                                            checked={formData.is_time_metric}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    handleChange("new_record_unit", "Tiempo");
                                                }
                                                handleChange("is_time_metric", e.target.checked);
                                            }}
                                        />
                                        <label
                                            className="training-form-modal-check-label"
                                            htmlFor="flexCheckDefault2"
                                        >
                                            ¿Es una métrica de tiempo?
                                        </label>
                                    </div>

                                    <div className="training-form-modal-form-check form-check">
                                        <input
                                            className="training-form-modal-checkbox form-check-input"
                                            type="checkbox"
                                            id="flexCheckInverse"
                                            checked={formData.is_inverse_metric}
                                            onChange={(e) => handleChange("is_inverse_metric", e.target.checked)}
                                        />
                                        <label
                                            className="training-form-modal-check-label"
                                            htmlFor="flexCheckInverse"
                                        >
                                            ¿Es una métrica inversa?
                                            <Popover
                                                title="¿Qué es una métrica inversa?"
                                                description="En Elevva usamos métricas inversas cuando queremos que los valores más bajos sean los mejores dentro del ranking. Por ejemplo: en una prueba de tiempo, el usuario con menor tiempo ocupará el puesto"
                                            >
                                                <svg className="ms-1" width="17" height="17" viewBox="0 0 17 17" fill="none"
                                                     xmlns="http://www.w3.org/2000/svg">
                                                    <circle cx="8.5" cy="8.5" r="8.5" fill="#3177BE"/>
                                                    <path
                                                        d="M8.68771 4.28522C9.89233 4.28522 11.0243 4.75355 11.8758 5.60505C12.7273 6.45655 13.1957 7.58855 13.1957 8.79317C13.1957 9.9978 12.7273 11.1298 11.8758 11.9813C11.0243 12.8328 9.89233 13.3011 8.68771 13.3011C7.48308 13.3011 6.35108 12.8328 5.49958 11.9813C4.64808 11.1298 4.17975 9.9978 4.17975 8.79317C4.17975 7.58855 4.64808 6.45655 5.49958 5.60505C6.35108 4.75355 7.48308 4.28522 8.68771 4.28522ZM8.68771 3.58398C5.81013 3.58398 3.47852 5.9156 3.47852 8.79317C3.47852 11.6707 5.81013 14.0024 8.68771 14.0024C11.5653 14.0024 13.8969 11.6707 13.8969 8.79317C13.8969 5.9156 11.5653 3.58398 8.68771 3.58398Z"
                                                        fill="white"/>
                                                    <path
                                                        d="M8.52797 5.64C8.90664 5.64 9.24264 5.696 9.53597 5.808C9.8293 5.92 10.0586 6.08533 10.224 6.304C10.3893 6.51733 10.472 6.78133 10.472 7.096C10.472 7.416 10.4026 7.68 10.264 7.888C10.1306 8.09067 9.9493 8.24533 9.71997 8.352C9.49064 8.45333 9.2373 8.52 8.95997 8.552L8.90397 9.304H7.90397L7.91197 8.096C8.2693 8.05333 8.54664 7.99733 8.74397 7.928C8.94664 7.85333 9.08797 7.76 9.16797 7.648C9.2533 7.536 9.29597 7.40267 9.29597 7.248C9.29597 7.09867 9.26397 6.97333 9.19997 6.872C9.1413 6.77067 9.05064 6.69333 8.92797 6.64C8.8053 6.58667 8.64797 6.56 8.45597 6.56C8.17864 6.56 7.9573 6.64 7.79197 6.8C7.62664 6.95467 7.5253 7.176 7.48797 7.464L6.44797 7.064C6.51197 6.78667 6.6293 6.54133 6.79997 6.328C6.97064 6.10933 7.1973 5.94133 7.47997 5.824C7.76797 5.70133 8.1173 5.64 8.52797 5.64ZM8.41597 9.84C8.62397 9.84 8.7893 9.896 8.91197 10.008C9.03464 10.12 9.09597 10.2693 9.09597 10.456C9.09597 10.648 9.03464 10.8 8.91197 10.912C8.7893 11.0187 8.62397 11.072 8.41597 11.072C8.20797 11.072 8.04264 11.0187 7.91997 10.912C7.7973 10.8 7.73597 10.648 7.73597 10.456C7.73597 10.2693 7.7973 10.12 7.91997 10.008C8.04264 9.896 8.20797 9.84 8.41597 9.84Z"
                                                        fill="white"/>
                                                </svg>
                                            </Popover>
                                        </label>
                                    </div>
                                </div>
                            </>
                        )}

                        {((!formData.has_record && !formData.is_duplicated) ||
                            (!formData.has_record &&
                                !formData.is_new_record &&
                                !formData.is_duplicated)) && (
                            <>
                                <div ref={dropdownRef} className="dropdown-center w-100">
                                    <button
                                        className="training-form-modal-dropdown dropdown-toggle btn w-100"
                                        type="button"
                                        aria-expanded={showOptions}
                                        onClick={() => setShowOptions((prev) => !prev)}
                                    >
                                        Opciones Avanzadas
                                    </button>

                                    {showOptions && (
                                        <ul
                                            className="dropdown-menu w-100 p-2 gap-2 show"
                                            style={{maxHeight: "50vh", overflowY: "auto"}}
                                        >
                                            {ADVANCED_OPTIONS.map(({label, key, options}) => (
                                                <li key={key} className="mb-2">
                                                    <label className="training-form-modal-form-label form-label mb-1">
                                                        {label}
                                                    </label>
                                                    <select
                                                        className="form-select training-form-modal-dropdown"
                                                        value={formData[key] || ""}
                                                        onChange={(e) => handleChange(key, e.target.value)}
                                                    >
                                                        {options.map(([value, text]) => (
                                                            <option key={value} value={value}>
                                                                {text}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </>
                        )}

                        {(!formData.is_duplicated ||
                            formData.has_record ||
                            formData.is_new_record) && (
                            <textarea
                                className="training-form-modal-textarea p-2"
                                rows={4}
                                placeholder="Describe la actividad de este día..."
                                value={formData.description}
                                onChange={(e) => handleChange("description", e.target.value)}
                            ></textarea>
                        )}

                        {formData.is_duplicated && (
                            <>
                                <div
                                    className="d-flex flex-column align-items-start justify-content-between w-100 gap-2">
                                    <div className="training-form-modal-line"></div>

                                    <div
                                        className="d-flex flex-row align-items-start justify-content-between w-100 gap-2">
                                        <label className="training-form-modal-label-date align-self-center">
                                            Buscar Entrenamiento
                                        </label>

                                        <input
                                            type="date"
                                            className="input-style-elevva training-form-modal-input w-70"
                                            value={searchDate}
                                            onChange={(e) => setSearchDate(e.target.value)}
                                            placeholder="Fecha del entrenamiento"
                                        />
                                    </div>

                                    <div
                                        className="training-form-modal-duplicate-container d-flex flex-wrap gap-3 justify-content-start p-1">
                                        <div className="row gx-1 gy-1 w-100">
                                            {duplicateResults.map((item) => (
                                                <div key={item.id} className="col-12 col-md-6">
                                                    <TrainingCard
                                                        item={item}
                                                        id={item.id}
                                                        date={item.launch_date}
                                                        time={item.launch_time}
                                                        type={item.class_name}
                                                        coach={item.teacher_name}
                                                        status={item.status}
                                                        trainingPlanId={trainingPlanId}
                                                        onRefresh={onRefresh}
                                                        isSelectable={true}
                                                        selectedId={selectedDuplicateId}
                                                        onSelect={(item) => {
                                                            setSelectedDuplicateId(item.id);
                                                            setSelectedDuplicate(item);
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        <input
                            type={inputType}
                            placeholder="Fecha del entrenamiento"
                            className="input-style-elevva training-form-modal-input w-70"
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            id="date"
                            value={formData.launch_date}
                            onChange={(e) => {
                                handleChange("launch_date", e.target.value);
                            }}
                        />

                        <div className="d-flex flex-row align-items-start justify-content-between w-100 gap-2">
                            <label className="training-form-modal-label-date align-self-center">
                                Hora de publicación
                            </label>

                            <input
                                type="time"
                                className="training-form-modal-time-input p-2"
                                value={formData.launch_time || "00:00"}
                                onChange={(e) => handleChange("launch_time", e.target.value)}
                            />
                        </div>

                        <button
                            className={`training-form-modal-submit ${
                                isFormValid ? "" : "disabled"
                            } mt-3 align-self-center`}
                            disabled={!isFormValid}
                            onClick={() => handleSubmit(false)}
                        >
                            {sessionId ? "Actualizar" : "Crear"}
                        </button>
                    </div>
                </div>
            </div>

            <TrainingInfoModal
                show={infoModalVisible}
                onClose={() => setInfoModalVisible(false)}
                title={infoModalConfig.title}
                description={infoModalConfig.description}
                primaryAction={infoModalConfig.primaryAction}
                secondaryAction={infoModalConfig.secondaryAction}
            />
        </>
    );
};

export default TrainingFormModal;
